import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CLINICAL_TIME_DIALOG_SIZE } from 'src/app/dialogs/clinical-time-dialog.component';
import { ClinicalTime } from 'src/app/models/clinical.model';
import { Patient } from 'src/app/models/patient.model';
import { ChartDraftService } from 'src/app/services/chart-draft/chart-draft.service';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { TimeTrackingState } from './time-tracking-states';

@Component({
  selector: 'app-time-tracking-modal',
  templateUrl: './time-tracking-modal.component.html',
  styleUrls: ['./time-tracking-modal.component.scss'],
})
export class TimeTrackingModalComponent implements OnInit, OnDestroy {
  showSaveForm = false;
  saveTimeData;
  oneMinuteInSeconds = 60;
  /** The patient's clinic has CCM care plan time on: the time is saved from a note, so "Write note to save time" replaces "Save Time". */
  timeFromNote = false;
  private unsubscribe$ = new Subject<void>();
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      body: string;
      title: string;
      patient: Patient;
      timeSpent: number;
      discardTimeButton?: string;
      saveTimeButton?: string;
      keepTimeButton?: string;
    },
    public dialogRef: MatDialogRef<TimeTrackingModalComponent>,
    private chartDraftService: ChartDraftService,
    private clientsService: ClientsService,
    private breakpointObserver: BreakpointObserver
  ) {}

  @HostListener('keydown.space', ['$event'])
  onSpaceKeyDown(event: KeyboardEvent) {
    event.stopPropagation();
    this.keepTracking();
  }

  ngOnInit(): void {
    this.clientsService
      .isCcmCarePlanTimeEnabled$(this.data?.patient?.client_responsible_id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((enabled) => (this.timeFromNote = enabled));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /** Stays on the chart, still timing, with the note panel open: the time is saved once the note is. */
  writeNote(): void {
    this.dialogRef.close(TimeTrackingState.WRITE_NOTE);
  }

  openSaveTimeForm(): void {
    this.showSaveForm = true;
    this.sizeForSaveForm(true);
    const newTime = {
      category: 'rpms',
      minutes: 0,
      seconds: 0,
      timeStamp: new Date(),
      user_id: this.data.patient.user_id,
      excluded: false,
    } as ClinicalTime;
    newTime.minutes =
      this.data.timeSpent > this.oneMinuteInSeconds
        ? (this.data.timeSpent - (this.data.timeSpent % this.oneMinuteInSeconds)) / this.oneMinuteInSeconds
        : 0;
    newTime.seconds = this.data.timeSpent % this.oneMinuteInSeconds;
    this.saveTimeData = {
      time: {
        minutes: newTime.minutes,
        mins: newTime.minutes,
        secs: newTime['seconds'],
        category: newTime.category,
        timeStamp: newTime.timeStamp,
        user_id: newTime.user_id,
        patient_id: newTime.patient_id,
        excluded: newTime.excluded,
        contact: newTime.contact,
      },
      isNew: true,
    };
  }

  closeSaveTimeForm(): void {
    this.showSaveForm = false;
    this.sizeForSaveForm(false);
  }

  keepTracking(): void {
    this.dialogRef.close(TimeTrackingState.KEEP_TRACKING);
  }

  discardTime(): void {
    // The work session is being discarded — clear any preserved chart draft for this patient.
    const patientId = this.data?.patient?.user_id;
    if (patientId) {
      this.chartDraftService.deleteDraft(patientId).subscribe({
        error: (err) => console.error('[ChartDraft] Failed to delete draft after time discard', err),
      });
    }
    this.dialogRef.close(TimeTrackingState.DISCARD);
  }

  closeAfterSaveTime(event: boolean): void {
    if (event) {
      this.dialogRef.close(TimeTrackingState.SAVE_TIME);
    } else {
      this.dialogRef.close(TimeTrackingState.NOACTION);
    }
  }

  /** The save form gets the clinical time dialog's size; on a phone the dialog already fills the screen. */
  private sizeForSaveForm(showsForm: boolean): void {
    if (this.breakpointObserver.isMatched(Breakpoints.XSmall)) {
      return;
    }
    const { width, height, panelClass } = CLINICAL_TIME_DIALOG_SIZE;
    if (showsForm) {
      this.dialogRef.addPanelClass(panelClass).updateSize(width, height);
    } else {
      this.dialogRef.removePanelClass(panelClass).updateSize();
    }
  }
}
